"use client";

import { useSyncExternalStore } from "react";

import { INJECTED_BADGE_PHONE_MAX_WIDTH } from "@/lib/badge";

/**
 * The "Remix with Panda" badge's own phone breakpoint, as a media query.
 *
 * Below it the badge tightens its padding, height and type so it can never
 * cover the generated app's above-the-fold content or the mobile chat controls
 * (PANDA-1251) — the same switch the injected badge's CSS makes in a published
 * build, kept on the one width both are defined by.
 */
const PHONE_QUERY = `(max-width: ${INJECTED_BADGE_PHONE_MAX_WIDTH}px)`;

function subscribe(callback: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia(PHONE_QUERY);
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

/** Guarded the same way `subscribe` is: an environment without `matchMedia`
 *  (jsdom, and anything rendering this outside a browser) can answer "is this a
 *  phone?" only with the default, and throwing here would take the whole
 *  preview frame down with it. */
function getSnapshot() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(PHONE_QUERY).matches;
}

/** Desktop (full badge) on the server / during hydration; the client snapshot
 *  takes over immediately after. */
function getServerSnapshot() {
  return false;
}

/**
 * Whether the BROWSER WINDOW is at the badge's phone breakpoint.
 *
 * The window, deliberately, not the preview box: the overlay badge
 * (components/built-with-badge.tsx) is rendered by the parent document, so the
 * parent's media query is the one it resolves — a 375px device-view preview
 * inside a wide desktop window still draws it at the desktop geometry. The
 * badge baked into a published app is the opposite case and reads the iframe's
 * width instead (INJECTED_BADGE_PHONE_MAX_WIDTH, compared against
 * `previewBoxWidth`).
 *
 * It lives here rather than beside the badge because the preview frame needs
 * the same answer — to know how tall a column the badge occupies before it
 * stacks the demo-limits pill above it — and importing it from the component
 * would put a layout constant inside every test that mocks the badge away.
 *
 * `useSyncExternalStore` (same pattern as usePrefersReducedMotion) keeps the
 * subscription outside React state and in sync on resize.
 */
export function useBadgePhoneViewport(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
